// THE OBLIGATION LADDER — what the next gate still wants from this register.
//
// Five conditions, in the order the gate engine already checks them. The page names the
// OBLIGATION and never prints a gate letter, because there are two different G3s in this
// codebase: `registerQueries.coverageReport` calls "every AC has a test case" G3, while
// `utils/gates.ts` case 'G3' is "SOW signed". Two different G3s must never appear on one page,
// and Lifecycle owns the letters (register IMPLEMENTATION §2; FEASIBILITY F6.3). Nothing here
// renames anything server-side — it simply refuses to repeat an ambiguous label.
//
// SHAPE: every rung is a `Move` (the shared minimum from queueRules.ts — imported as a TYPE, so
// nothing from src/server reaches the browser bundle), narrowed with the page-local act that
// clears it. Weight is declaration order × 10, the convention for an unweighted ladder.
//
// SENTENCES: where the coverage endpoint has already composed a finished English blocker for a
// condition, that sentence is rendered UNEDITED (B7) and this module composes nothing. The
// fallback line is used only when the endpoint did not answer or the condition is met — i.e.
// when there is no server sentence to quote.
//
// DEGRADATION: a rung whose source did not answer is dropped from the ladder rather than
// rendered as met. A page that answers a question the endpoint refused to answer is the exact
// failure this codebase is written against (B8).

import type { Move } from '../../../server/utils/queueRules.js';
import { makeT, makeTp, type Vars } from '../../../shared/i18n.js';
import type { CoverageReport, RegisterRow } from '../../api/register.js';
import './ladder.de.js';
import './ladder.fr.js';

/** The translator the ladder speaks through — `useLocale()`'s `{ t, tp }`; English when absent. */
export interface LadderLocale {
  t: (key: string, vars?: Vars) => string;
  tp: (n: number, one: string, other: string, vars?: Vars) => string;
}
const EN_T = makeT(null);
const EN: LadderLocale = { t: EN_T, tp: makeTp(EN_T) };

export type RungKey = 'held' | 'queue' | 'funded' | 'justified' | 'proven';

/** The complete ladder, in order. A rung MISSING from a built ladder was not computed. */
export const ALL_RUNGS: readonly RungKey[] = ['held', 'queue', 'funded', 'justified', 'proven'];

/** How each condition is named when it has to be spoken about in a sentence — wrap with t() at the render site. */
export const RUNG_NOUN: Record<RungKey, string> = {
  held: 'held',
  queue: 'the review queue',
  funded: 'funded',
  justified: 'justified',
  proven: 'proven',
};

/** The three page-local destinations a rung can name. A route is a property of the CONDITION. */
export type RungAct =
  | { kind: 'filter'; filter: { status?: string; priority?: string; gap?: string; live?: boolean } }
  | { kind: 'tab'; tab: 'requirements' | 'deliverables' | 'test-cases' }
  | { kind: 'page'; page: 'proposals' | 'agent' };

export interface Rung extends Move {
  key: RungKey;
  /** True only when the condition is positively satisfied. Never true because a source is down. */
  met: boolean;
  /** The link text on the rung, in the operator's words: "the 2 rows still proposed". */
  routeLabel: string;
  act: RungAct;
  /** The masthead's one filled control while this rung is binding. Null when there is nothing. */
  primary: { label: string; kind: 'confirm-drafts' | 'filter' | 'tab' | 'page' | 'test-case-pass' } | null;
}

/** The facts the ladder needs. Everything here is already on the wire; nothing is re-fetched. */
export interface LadderFacts {
  rows: RegisterRow[];
  /** Null when GET /register/coverage did not answer — three rungs then read `unknown`. */
  coverage: CoverageReport | null;
}

const HELD_STATUSES = new Set(['confirmed', 'baselined', 'delivered', 'accepted']);

/** In play = anything not descoped. Exclusions ARE scope — what the contract says is NOT included. */
export const inPlay = (r: RegisterRow): boolean => r.status !== 'descoped';

export interface RegisterTallies {
  inPlay: RegisterRow[];
  held: RegisterRow[];
  drafts: RegisterRow[];
  musts: RegisterRow[];
  exclusions: RegisterRow[];
  descoped: RegisterRow[];
  sourced: RegisterRow[];
  unsourced: RegisterRow[];
}

/**
 * The row-derived half of the ledger. These four counts come from the grid payload the page
 * already holds, so they survive a coverage outage — which is the whole point of splitting the
 * two fetches (IMPLEMENTATION §1, the F5.2 rule).
 */
export function tally(rows: RegisterRow[]): RegisterTallies {
  const live = rows.filter(inPlay);
  return {
    inPlay: live,
    held: live.filter((r) => HELD_STATUSES.has(r.status)),
    drafts: live.filter((r) => r.status === 'draft'),
    musts: live.filter((r) => r.priority === 'must'),
    exclusions: rows.filter((r) => r.type === 'out_of_scope'),
    descoped: rows.filter((r) => !inPlay(r)),
    sourced: live.filter((r) => r.evidence_count > 0),
    unsourced: live.filter((r) => r.evidence_count === 0),
  };
}

/** The server's own sentence for this condition, or null when it did not compose one. */
const blocker = (cov: CoverageReport | null, gate: string, match: RegExp): string | null =>
  cov?.gates?.[gate]?.blockers.find((b) => match.test(b)) ?? null;

/**
 * Build the ladder. Rungs whose source is absent are OMITTED — the caller states that the
 * coverage endpoint did not answer, once, and the omitted conditions are not claimed either way.
 */
export function buildLadder({ rows, coverage }: LadderFacts, loc: LadderLocale = EN): Rung[] {
  const { t: tr, tp } = loc;
  const t = tally(rows);
  const out: Rung[] = [];
  const push = (r: Omit<Rung, 'weight'>) => out.push({ ...r, weight: out.length * 10 });

  // 1 · HELD — every row stands behind an operator. Row-derived: survives a coverage outage.
  push({
    key: 'held',
    rule: 'register-held',
    met: t.inPlay.length > 0 && t.drafts.length === 0,
    title: tr('Every requirement held'),
    why:
      blocker(coverage, 'G1', /still in draft/) ??
      (t.inPlay.length === 0
        ? tr('The register is empty — nothing is on record to confirm.')
        : tr('{held} of {total} requirements held', { held: t.held.length, total: t.inPlay.length })),
    route: { kind: 'in-page', anchor: 'reg-rows' },
    refs: t.drafts.map((r) => r.ref),
    tone: t.drafts.length ? 'warn' : 'ok',
    routeLabel: t.drafts.length
      ? tp(t.drafts.length, 'the {n} row still proposed', 'the {n} rows still proposed')
      : tr('All rows'),
    act: { kind: 'filter', filter: { status: 'draft' } },
    primary: t.drafts.length ? { label: tr('Confirm {n} proposed', { n: t.drafts.length }), kind: 'confirm-drafts' } : null,
  });

  // 2 · QUEUE — a pending proposal is scope nobody has ruled on yet.
  const pending = coverage ? Number(coverage.totals.pending_proposals ?? 0) : null;
  if (pending !== null) {
    push({
      key: 'queue',
      rule: 'register-queue',
      met: pending === 0,
      title: tr('Zero pending proposals'),
      why:
        blocker(coverage, 'G1', /pending proposal/) ??
        (pending === 0 ? tr('The review queue is clear.') : tp(pending, '{n} proposal awaiting review', '{n} proposals awaiting review')),
      route: { kind: 'screen', screen: 'proposals' },
      tone: pending ? 'warn' : 'ok',
      routeLabel: tr('Proposals'),
      act: { kind: 'page', page: 'proposals' },
      primary: pending ? { label: tp(pending, 'Review {n} proposal', 'Review {n} proposals'), kind: 'page' } : null,
    });
  }

  // 3 · FUNDED — a MUST with no deliverable is unfunded work, and it blocks the estimate.
  //
  // The list is `must_requirements_without_deliverable`, which is the GATE'S own query
  // (registerQueries.ts carries the comment explaining why the other list differs). Known
  // divergence, stated rather than hidden: the gate does not exclude a mapping to a DROPPED or
  // cross-engagement deliverable, so such a row would read funded here and unfunded at the gate
  // (FEASIBILITY G-12). No row in the live database triggers it today.
  const unfunded = coverage?.must_requirements_without_deliverable ?? null;
  if (unfunded) {
    const mustTotal = Number(coverage!.totals.must ?? t.musts.length);
    push({
      key: 'funded',
      rule: 'register-funded',
      met: mustTotal > 0 && unfunded.length === 0,
      title: tr('Every MUST requirement maps to a deliverable'),
      why:
        blocker(coverage, 'G2', /no deliverable/) ??
        (mustTotal === 0
          ? tr('No MUST requirement is on the register — the gate wants at least one.')
          : tr('{mapped} of {total} MUST requirements mapped', { mapped: mustTotal - unfunded.length, total: mustTotal })),
      route: { kind: 'in-page', anchor: 'reg-rows' },
      refs: unfunded.map((r) => r.ref),
      tone: unfunded.length ? 'warn' : 'ok',
      routeLabel: tr('the unfunded musts'),
      // in-play only: the gate's own qualifier, so the rows shown are the rows counted
      act: { kind: 'filter', filter: { gap: 'del', priority: 'must', live: true } },
      primary: unfunded.length ? { label: tr('Map the unfunded musts'), kind: 'filter' } : null,
    });
  }

  // 4 · JUSTIFIED — a deliverable no requirement asks for is work nobody bought.
  const unjustified = coverage?.deliverables_without_requirement ?? null;
  if (unjustified) {
    const dels = Number(coverage!.totals.deliverables ?? 0);
    push({
      key: 'justified',
      rule: 'register-justified',
      met: dels > 0 && unjustified.length === 0,
      title: tr('No deliverable without a requirement'),
      why:
        blocker(coverage, 'G2', /no requirement|no deliverables defined/) ??
        (dels === 0
          ? tr('No deliverable is defined — there is nothing for a requirement to map to.')
          : tr('{n} of {total} deliverables carry at least one requirement', { n: dels - unjustified.length, total: dels })),
      route: { kind: 'in-page', anchor: 'reg-rows' },
      refs: unjustified.map((d) => d.ref),
      tone: unjustified.length || dels === 0 ? 'warn' : 'ok',
      routeLabel: tr('Deliverables'),
      act: { kind: 'tab', tab: 'deliverables' },
      primary: unjustified.length || dels === 0 ? { label: tr('Open the deliverables'), kind: 'tab' } : null,
    });
  }

  // 5 · PROVEN — a criterion with no test case is something the client cannot pass or fail.
  const unproven = coverage?.acs_without_test_case ?? null;
  if (unproven) {
    const acs = Number(coverage!.totals.acceptance_criteria ?? 0);
    push({
      key: 'proven',
      rule: 'register-proven',
      met: acs > 0 && unproven.length === 0,
      title: tr('Every acceptance criterion has a test case'),
      why:
        blocker(coverage, 'G3', /test case/) ??
        (acs === 0
          ? tr('No acceptance criterion is on record yet — nothing here is testable.')
          : tr('{n} of {total} criteria have a test case', { n: acs - unproven.length, total: acs })),
      route: { kind: 'in-page', anchor: 'reg-rows' },
      refs: unproven.map((a) => a.ref),
      tone: unproven.length ? 'warn' : 'ok',
      routeLabel: tr('Test cases'),
      act: { kind: 'tab', tab: 'test-cases' },
      primary: unproven.length
        ? { label: tp(unproven.length, 'Propose test cases for {n} criterion', 'Propose test cases for {n} criteria'), kind: 'test-case-pass' }
        : null,
    });
  }

  return out;
}

/** The first unmet rung — the one clause the page leads with, and the ledger cell it caps. */
export const bindingRung = (ladder: Rung[]): Rung | null => ladder.find((r) => !r.met) ?? null;

/** Sort is declaration order (weight ASC), then rule name — the shared ladder comparator. */
export const sortLadder = (ladder: Rung[]): Rung[] =>
  [...ladder].sort((a, b) => a.weight - b.weight || a.rule.localeCompare(b.rule));
